import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Bug, Loader2, Send } from "lucide-react";

// APP_VERSION could come from build info; we ship a static tag for now.
const APP_VERSION = "phase-21";

/** Best-effort browser/device/OS fingerprint. Never contains provider info. */
function collectClientContext() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const detect = (list) => list.find((k) => ua.includes(k)) || "";
  const browser =
    detect(["Firefox", "Edg", "Chrome", "Safari", "Opera"]) || "Unknown";
  const device = /Mobi|Android|iPhone|iPad/.test(ua) ? "Mobile" : "Desktop";
  const os =
    detect(["Windows", "Mac OS X", "Mac", "Linux", "Android", "iPhone", "iPad"]) ||
    "Unknown";
  return { browser, device, os, app_version: APP_VERSION };
}

/**
 * ReportProblemDialog
 * Props:
 *   project      — the failed project object (must have id, error_code, error)
 *   open, onOpenChange
 *   onSubmitted  — optional callback fired after a successful POST
 */
export default function ReportProblemDialog({ project, open, onOpenChange, onSubmitted }) {
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!desc.trim()) {
      toast.error("Please describe what went wrong (a few words is enough).");
      return;
    }
    setBusy(true);
    try {
      const ctx = collectClientContext();
      await api.post("/reports/problem", {
        project_id: project.id,
        description: desc.trim(),
        ...ctx,
      });
      toast.success("Thanks — your report has been sent to our team.");
      setDesc("");
      onOpenChange?.(false);
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send report.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121214] border-white/10 sm:max-w-md" data-testid="report-problem-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Bug className="w-5 h-5 text-amber-400" strokeWidth={1.75} />
            Report a problem
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm leading-relaxed">
            Tell us what you were trying to do. We&apos;ll get a signal with the
            technical details attached automatically.
          </DialogDescription>
        </DialogHeader>

        {/* Auto-collected info — user-visible summary */}
        <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 space-y-1.5 text-[11px] font-mono-tech text-zinc-400" data-testid="report-context">
          <div className="flex justify-between gap-3">
            <span className="text-zinc-500">Project</span>
            <span className="text-zinc-200 truncate max-w-[220px]">{project.id}</span>
          </div>
          {project.error_code && (
            <div className="flex justify-between gap-3">
              <span className="text-zinc-500">Error code</span>
              <span className="text-zinc-200">{project.error_code}</span>
            </div>
          )}
          {project.error && (
            <div className="flex justify-between gap-3">
              <span className="text-zinc-500">Message</span>
              <span className="text-zinc-200 truncate max-w-[240px]">{project.error}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="pr-desc" className="text-xs text-zinc-400 font-medium">
            What happened?
          </label>
          <Textarea
            id="pr-desc"
            data-testid="report-description"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="I tried to generate a video of 5 photos but it failed at the review step…"
            rows={5}
            className="bg-[#0b0b0d] border-white/10 focus:border-amber-500/50 resize-none"
          />
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            className="text-zinc-400 hover:text-white hover:bg-white/5"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            data-testid="report-submit"
            disabled={busy || !desc.trim()}
            className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
          >
            {busy
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Send className="w-4 h-4 mr-2" strokeWidth={2} />}
            Send report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
